/* =============================================================
   Meshack Nartey — Portfolio
   Progressive enhancements. Every block guards its own elements
   so the same file can be shared across all pages.
   ============================================================= */

(function () {
  "use strict";

  var prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  /* ---------- Theme ---------- */

  (function initTheme() {
    var toggle = document.querySelector(".theme-toggle");
    if (!toggle) return;

    var darkQuery = window.matchMedia("(prefers-color-scheme: dark)");

    function resolvedTheme() {
      var explicit = document.documentElement.getAttribute("data-theme");
      if (explicit === "dark" || explicit === "light") return explicit;
      return darkQuery.matches ? "dark" : "light";
    }

    // Keep the browser chrome colour in sync once a theme is pinned.
    function syncThemeColor(theme) {
      var color = theme === "dark" ? "#0b0c0e" : "#ffffff";
      document
        .querySelectorAll('meta[name="theme-color"]')
        .forEach(function (meta) {
          meta.setAttribute("content", color);
        });
    }

    toggle.addEventListener("click", function () {
      var next = resolvedTheme() === "dark" ? "light" : "dark";
      document.documentElement.setAttribute("data-theme", next);
      syncThemeColor(next);
      try {
        localStorage.setItem("theme", next);
      } catch (e) {
        /* storage unavailable — theme still applies for this page */
      }
    });
  })();

  /* ---------- Mobile navigation ---------- */

  (function initNav() {
    var toggle = document.querySelector(".nav-toggle");
    var menu = document.getElementById("nav-menu");
    if (!toggle || !menu) return;

    var iconPath = toggle.querySelector("svg path");
    var MENU_ICON = "M3 6h18M3 12h18M3 18h18";
    var CLOSE_ICON = "M18 6 6 18M6 6l12 12";

    function setOpen(open) {
      menu.classList.toggle("open", open);
      toggle.setAttribute("aria-expanded", String(open));
      toggle.setAttribute("aria-label", open ? "Close navigation" : "Open navigation");
      if (iconPath) iconPath.setAttribute("d", open ? CLOSE_ICON : MENU_ICON);
    }

    toggle.addEventListener("click", function () {
      setOpen(!menu.classList.contains("open"));
    });

    menu.addEventListener("click", function (e) {
      if (e.target.closest(".nav-link")) setOpen(false);
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && menu.classList.contains("open")) {
        setOpen(false);
        toggle.focus();
      }
    });

    // Reset state if the viewport grows past the mobile breakpoint.
    window.matchMedia("(min-width: 881px)").addEventListener("change", function (e) {
      if (e.matches) setOpen(false);
    });
  })();

  /* ---------- Navbar border on scroll ---------- */

  (function initNavbarScroll() {
    var navbar = document.querySelector(".navbar");
    if (!navbar) return;

    var ticking = false;

    function update() {
      navbar.classList.toggle("is-scrolled", window.scrollY > 8);
      ticking = false;
    }

    window.addEventListener(
      "scroll",
      function () {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(update);
        }
      },
      { passive: true }
    );

    update();
  })();

  /* ---------- Active section highlighting ---------- */

  (function initScrollSpy() {
    var sections = Array.prototype.slice.call(
      document.querySelectorAll("main section[id]")
    );
    if (!sections.length) return;

    var links = {};
    document.querySelectorAll('.nav-link[href^="#"]').forEach(function (link) {
      links[link.getAttribute("href").slice(1)] = link;
    });
    if (!Object.keys(links).length) return;

    var visible = new Set();

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        });

        // Highlight the topmost section currently in the viewport band.
        var active = sections
          .map(function (s) {
            return s.id;
          })
          .filter(function (id) {
            return visible.has(id);
          })[0];

        Object.keys(links).forEach(function (id) {
          links[id].classList.toggle("active", id === active);
        });
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );

    sections.forEach(function (section) {
      observer.observe(section);
    });
  })();

  /* ---------- Reveal on scroll ---------- */

  (function initReveal() {
    var pending = Array.prototype.slice.call(
      document.querySelectorAll(".reveal")
    );
    if (!pending.length) return;

    function revealAll() {
      pending.forEach(function (el) {
        el.classList.add("visible");
      });
      pending = [];
    }

    if (prefersReducedMotion) {
      revealAll();
      return;
    }

    var ticking = false;

    // Deliberately scroll-driven rather than IntersectionObserver: anything at
    // or above the trigger line is revealed, so content can never get stranded
    // at opacity 0 if an observer callback is missed.
    function update() {
      ticking = false;
      var trigger = window.innerHeight - 60;
      var batch = 0;

      pending = pending.filter(function (el) {
        if (el.getBoundingClientRect().top > trigger) return true;
        var delay = batch++ * 70;
        setTimeout(function () {
          el.classList.add("visible");
        }, delay);
        return false;
      });

      if (!pending.length) {
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onScroll);
      }
    }

    function onScroll() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    update();
  })();

  /* ---------- Gallery lightbox ---------- */

  (function initLightbox() {
    var lightbox = document.getElementById("lightbox");
    var image = document.getElementById("lightbox-image");
    if (!lightbox || !image) return;

    var closeBtn = lightbox.querySelector(".lightbox-close");
    var lastFocused = null;

    function open(src, alt) {
      lastFocused = document.activeElement;
      image.src = src;
      image.alt = alt || "";
      lightbox.classList.add("open");
      lightbox.setAttribute("aria-hidden", "false");
      document.body.style.overflow = "hidden";
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      lightbox.classList.remove("open");
      lightbox.setAttribute("aria-hidden", "true");
      document.body.style.overflow = "";
      if (lastFocused) lastFocused.focus();
      setTimeout(function () {
        // removeAttribute rather than src = "" — an empty src re-requests the page.
        if (!lightbox.classList.contains("open")) image.removeAttribute("src");
      }, 250);
    }

    document.querySelectorAll(".post-photo").forEach(function (photo) {
      photo.addEventListener("click", function () {
        open(photo.currentSrc || photo.src, photo.alt);
      });
    });

    if (closeBtn) closeBtn.addEventListener("click", close);

    lightbox.addEventListener("click", function (e) {
      if (e.target === lightbox) close();
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && lightbox.classList.contains("open")) close();
    });
  })();

  /* ---------- Project group counts ---------- */

  (function initGroupCounts() {
    // Derived from the DOM so the counts can't drift when projects are
    // added or removed. The markup keeps a correct value as a no-JS fallback.
    document.querySelectorAll(".project-group").forEach(function (group) {
      var label = group.querySelector(".group-count");
      if (!label) return;
      var n = group.querySelectorAll(".project-row").length;
      label.textContent = n + (n === 1 ? " project" : " projects");
    });
  })();

  /* ---------- Footer year ---------- */

  (function initYear() {
    var year = String(new Date().getFullYear());
    document.querySelectorAll("[data-current-year]").forEach(function (el) {
      el.textContent = year;
    });
  })();
})();
